import { createContext, useContext, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Alert, Box, ButtonBase, CircularProgress, Menu, MenuItem, Snackbar } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { statusChangeMessages } from "src/constants";

// The second click of a double-click that opens a status's menu lands on the menu's backdrop, which mustn't close it
const DOUBLE_CLICK_MS = 500;

// Viewers who can't change any status
const noStatusChanges = {};

const cellKey = (id, field) => `${id}:${field}`;

// What status cells need from their grid: the statuses the viewer can change, the ones being saved, and the menu
const StatusGridContext = createContext({
  statusChanges: noStatusChanges,
  savingCells: [],
  menuCellKey: null,
  openMenu: () => false,
});

// A status the viewer can change is a dropdown: its label after a dot in the status's colour, then ▾. Any other is
// just its label
function StatusCell({ id, field, value, row, label, color, hasFocus }) {
  const { statusChanges, savingCells, menuCellKey, openMenu } = useContext(StatusGridContext);
  const key = cellKey(id, field);

  if (!statusChanges[field]) return label;

  const isSaving = savingCells.includes(key);
  const isMenuOpen = menuCellKey === key;

  return (
    <ButtonBase
      disableRipple
      // The grid moves focus between cells, so only the focused cell's dropdown is tabbable
      tabIndex={hasFocus ? 0 : -1}
      aria-haspopup="menu"
      aria-expanded={isMenuOpen}
      disabled={isSaving}
      onClick={(event) => openMenu({ id, field, value, row }, event.currentTarget)}
      sx={{
        width: "100%",
        height: 30,
        gap: 0.75,
        pl: 1,
        pr: 0.25,
        justifyContent: "flex-start",
        border: 1,
        borderColor: isMenuOpen ? "secondary.main" : "grey.400",
        borderRadius: 1,
        bgcolor: "background.paper",
        font: "inherit",
        fontSize: 13,
        color: "text.primary",
        "&:hover": { borderColor: isMenuOpen ? "secondary.main" : "text.primary" },
        "&.Mui-focusVisible": {
          borderColor: "secondary.main",
          boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.secondary.main}`,
        },
      }}
    >
      <Box
        component="span"
        sx={{ width: 8, height: 8, flexShrink: 0, borderRadius: "50%", bgcolor: color ? `${color}.main` : "grey.500" }}
      />
      <Box
        component="span"
        sx={{ flexGrow: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}
      >
        {label}
      </Box>
      {isSaving ? (
        <CircularProgress size={14} color="secondary" aria-label={statusChangeMessages.SAVING} sx={{ mx: 0.375 }} />
      ) : (
        <ArrowDropDown sx={{ color: "action.active" }} />
      )}
    </ButtonBase>
  );
}

// A column of statuses shown by their labels (labels: { [status]: label }) and filtered by picking one. In a
// StatusDataGrid, statuses the viewer can change are dropdowns, with a dot in the status's palette colour (colors:
// { [status]: colour })
export const statusColumn = ({ labels, colors = {}, ...column }) => ({
  type: "singleSelect",
  valueOptions: Object.entries(labels).map(([value, label]) => ({ value, label })),
  valueFormatter: ({ value }) => labels[value] || value || "-",
  renderCell: ({ id, field, value, row, formattedValue, hasFocus }) => (
    <StatusCell
      id={id}
      field={field}
      value={value}
      row={row}
      label={formattedValue}
      color={colors[value]}
      hasFocus={hasFocus}
    />
  ),
  ...column,
});

// The users and organisations pages' DataGrid, whose statuses master users change straight from the list: clicking a
// status's dropdown, double-clicking its cell or pressing Enter on it opens a menu of what it can be changed to.
// statusChanges maps each status field the viewer can change to those options ([{ value, label }]).
// onStatusChange(row, field, value) saves a change, rejecting with the server's { message } if it fails.
export default function StatusDataGrid({ statusChanges = noStatusChanges, onStatusChange, ...gridProps }) {
  // The cell whose menu is open. Kept while the menu closes, so its options don't disappear mid-transition
  const [menu, setMenu] = useState({ open: false, options: [] });
  const [savingCells, setSavingCells] = useState([]);
  const [error, setError] = useState({ open: false, message: "" });

  // Whether the menu opened: not for statuses the viewer can't change or that are being saved
  function openMenu({ id, field, value, row }, anchorEl) {
    const options = statusChanges[field];
    if (!options || savingCells.includes(cellKey(id, field))) return false;

    setMenu({ open: true, openedAt: Date.now(), anchorEl, options, id, field, value, row });
    return true;
  }

  // Double-click or Enter anywhere in a status cell: the menu opens under its dropdown
  function openCellMenu(params, event) {
    const anchorEl = event.currentTarget.querySelector("button") || event.currentTarget;
    if (!openMenu(params, anchorEl)) return;

    // Nothing for the grid to do with it, nor for the dropdown when it's what has focus
    event.defaultMuiPrevented = true;
    event.preventDefault();
  }

  function handleCellKeyDown(params, event) {
    if (event.key === "Enter") openCellMenu(params, event);
  }

  function closeMenu() {
    setMenu((current) => ({ ...current, open: false }));
  }

  function handleMenuClose(event, reason) {
    if (reason === "backdropClick" && Date.now() - menu.openedAt < DOUBLE_CLICK_MS) return;

    closeMenu();
  }

  async function changeStatus(value) {
    const { id, field, row } = menu;
    const key = cellKey(id, field);

    closeMenu();
    setSavingCells((cells) => [...cells, key]);

    try {
      await onStatusChange(row, field, value);
    } catch (rejection) {
      const { message } = rejection || {};
      setError({ open: true, message: typeof message === "string" ? message : statusChangeMessages.GENERIC_ERROR });
    }

    setSavingCells((cells) => cells.filter((cell) => cell !== key));
  }

  function closeError() {
    setError((current) => ({ ...current, open: false }));
  }

  const statusGrid = {
    statusChanges,
    savingCells,
    menuCellKey: menu.open ? cellKey(menu.id, menu.field) : null,
    openMenu,
  };

  return (
    <StatusGridContext.Provider value={statusGrid}>
      <DataGrid {...gridProps} onCellDoubleClick={openCellMenu} onCellKeyDown={handleCellKeyDown} />

      <Menu
        open={menu.open}
        anchorEl={menu.anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { mt: 0.5, minWidth: menu.anchorEl?.offsetWidth } }}
      >
        {/* The status it already has can't be picked again */}
        {menu.options.map(({ value, label }) => (
          <MenuItem
            key={value}
            selected={value === menu.value}
            disabled={value === menu.value}
            onClick={() => changeStatus(value)}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={error.open}
        autoHideDuration={8000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={closeError}
      >
        <Alert onClose={closeError} elevation={6} severity="error" sx={{ width: "100%" }}>
          {error.message}
        </Alert>
      </Snackbar>
    </StatusGridContext.Provider>
  );
}
